import { useState } from 'react';
import { Alert, Button, Tabs, Space, Spin } from 'antd';
import { Plus, List, PieChart } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { useProductionPeriodFilter } from '@/hooks/useProductionPeriodFilter';
import { useProductionStartDay } from '@/hooks/useProductionStartDay';
import { getRecentProductionPeriods } from '@/utils/productionPeriod';
import { FluxoCaixaTable } from '../components/FluxoCaixaTable';
import { FluxoCaixaFilters } from '../components/FluxoCaixaFilters';
import { FluxoCaixaResumo } from '../components/FluxoCaixaResumo';
import { FluxoCaixaResumoPeriodo } from '../components/FluxoCaixaResumoPeriodo';
import { FluxoCaixaGraficos } from '../components/FluxoCaixaGraficos';
import { FluxoCaixaCategoriaRelatorio } from '../components/FluxoCaixaCategoriaRelatorio';
import { FluxoCaixaExportButton } from '../components/FluxoCaixaExportButton';
import { FluxoCaixaEditModal } from '../components/FluxoCaixaEditModal';
import { FluxoCaixaCreateModal } from '../components/FluxoCaixaCreateModal';
import { useFluxoCaixa } from '../hooks/useFluxoCaixa';
import { useFluxoCaixaRelatorio } from '../hooks/useFluxoCaixaRelatorio';
import { FluxoCaixaLancamento } from '../types/fluxoCaixa';

type FluxoCaixaTab = 'lancamentos' | 'relatorios';

export function FluxoCaixaPage() {
  const { profile } = useAuth();
  const tenantId = profile?.tenant_id ? Number(profile.tenant_id) : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const { productionPeriodFilter, setProductionPeriodFilter } = useProductionPeriodFilter();
  const startDay = useProductionStartDay();
  const recentPeriods = getRecentProductionPeriods(12, startDay);

  const activeTab = (searchParams.get('tab') as FluxoCaixaTab) || 'lancamentos';

  const {
    lancamentos,
    resumo,
    periodoResumo,
    setPeriodoResumo,
    filtros,
    setFiltros,
    loadingTabela,
    loadingResumo,
    error,
    criar,
    atualizar,
    deletar,
  } = useFluxoCaixa();

  const relatorio = useFluxoCaixaRelatorio(tenantId);

  const [formKey, setFormKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedLancamento, setSelectedLancamento] = useState<FluxoCaixaLancamento | undefined>();
  const [editModalVisible, setEditModalVisible] = useState(false);

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab }, { replace: true });
  };

  const handleCriar = async (formData: Parameters<typeof criar>[0]) => {
    try {
      setSubmitting(true);
      await criar(formData);
      setFormKey(k => k + 1);
      await relatorio.recarregar();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (lancamento: FluxoCaixaLancamento) => {
    setSelectedLancamento(lancamento);
    setEditModalVisible(true);
  };

  const handleSaveEdit = async (id: number, data: Parameters<typeof atualizar>[1]) => {
    await atualizar(id, data);
    setEditModalVisible(false);
    setSelectedLancamento(undefined);
    await relatorio.recarregar();
  };

  const handleDelete = async (lancamento: FluxoCaixaLancamento) => {
    await deletar(lancamento.id);
    await relatorio.recarregar();
  };

  const tabItems = [
    {
      key: 'lancamentos',
      label: (
        <span className="inline-flex items-center gap-2 px-1">
          <List className="w-4 h-4" />
          Lançamentos
        </span>
      ),
      children: (
        <div className="pt-2">
          <p className="text-xs text-gray-500 dark:text-zinc-400 mb-4">
            Visualize, filtre e gerencie os lançamentos do período
          </p>
          <FluxoCaixaFilters filtros={filtros} onChange={setFiltros} />
          <FluxoCaixaTable
            lancamentos={lancamentos}
            loading={loadingTabela}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onNovoLancamento={() => setCreateModalVisible(true)}
          />
        </div>
      ),
    },
    {
      key: 'relatorios',
      label: (
        <span className="inline-flex items-center gap-2 px-1">
          <PieChart className="w-4 h-4" />
          Relatórios
        </span>
      ),
      children: (
        <div className="pt-2 space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              Gráficos e análise por categoria do fluxo de caixa
            </p>
            <FluxoCaixaExportButton
              lancamentos={lancamentos}
              filtros={filtros}
              disabled={loadingTabela}
            />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-zinc-200 mb-4">
              Visão geral
            </h3>
            <div className="rounded-xl bg-[#0d0d0f] p-4">
              <FluxoCaixaGraficos
                resumo={resumo}
                evolucao={relatorio.evolucao}
                loadingResumo={loadingResumo}
                loadingEvolucao={relatorio.loadingEvolucao}
              />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-zinc-200 mb-4">
              Por categoria
            </h3>
            <FluxoCaixaCategoriaRelatorio
              linhas={relatorio.linhas}
              periodo={relatorio.periodo}
              tipo={relatorio.tipo}
              loading={relatorio.loadingRelatorio}
              onPeriodoChange={relatorio.setPeriodo}
              onTipoChange={relatorio.setTipo}
            />
          </div>
        </div>
      ),
    },
  ];

  const productionPeriodValue = productionPeriodFilter
    ? `${productionPeriodFilter.year}-${String(productionPeriodFilter.month).padStart(2, '0')}`
    : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

  const handleProductionPeriodChange = (value: string) => {
    const [year, month] = value.split('-');
    setProductionPeriodFilter({ month: parseInt(month, 10), year: parseInt(year, 10) });
  };

  const headerExtra = (
    <Space>
      <FluxoCaixaResumoPeriodo periodo={periodoResumo} onChange={setPeriodoResumo} />
      <Button
        type="primary"
        icon={<Plus className="w-4 h-4" />}
        onClick={() => setCreateModalVisible(true)}
        className="bg-white/20 border-white/30 text-white hover:bg-white/30 hover:text-white font-medium"
      >
        Novo Lançamento
      </Button>
    </Space>
  );

  return (
    <Spin spinning={loadingTabela || loadingResumo} tip="Carregando fluxo de caixa...">
      <div className="flex-1 flex flex-col min-h-0 bg-[#f5f6f8] dark:bg-[#141414] transition-colors duration-300">
        <PageHeader
        heroic={true}
        title="Fluxo de Caixa"
        heroLabel="Gestão Financeira"
        productionPeriod={{
          value: productionPeriodValue,
          options: recentPeriods.map(p => ({
            label: p.label.charAt(0).toUpperCase() + p.label.slice(1),
            value: `${p.year}-${String(p.month).padStart(2, '0')}`,
          })),
          onChange: handleProductionPeriodChange,
        }}
        headerExtra={headerExtra}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-3 sm:px-4 md:px-6 xl:px-8 py-6 w-full max-w-[2000px] mx-auto">
          {error && (
            <Alert
              message="Erro ao carregar lançamentos"
              description={error.message}
              type="error"
              showIcon
              closable
              className="mb-6"
            />
          )}

          <div className="rounded-[16px] bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 p-6 shadow-sm">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={tabItems}
              className="[&_.ant-tabs-nav]:mb-4"
            />
          </div>
        </div>
      </div>

      <FluxoCaixaCreateModal
        visible={createModalVisible}
        loading={submitting}
        formKey={formKey}
        onSubmit={handleCriar}
        onCancel={() => setCreateModalVisible(false)}
      />

      <FluxoCaixaEditModal
        visible={editModalVisible}
        lancamento={selectedLancamento}
        onSave={handleSaveEdit}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedLancamento(undefined);
        }}
      />
      </div>
    </Spin>
  );
}

export default FluxoCaixaPage;
